import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { adminSidebar } from '@/config/sidebarConfig';
import { getOrganizationsApi } from '@/lib/api/organizationsApi';
import { getPaymentApi, getPaymentsApi, refundPaymentApi } from '@/lib/api/paymentsApi';
import { getAdminStatsApi } from '@/lib/api/usersApi';
import { type ExportColumn, type ExportFormat, datedFilename, exportData } from '@/lib/export/export';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { PAY_STATUS_CONFIG } from '@/lib/status-config';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, formatEur } from '@shared';
import { PaymentStatus } from '@shared/enums/domain.enum';
import type { GetPaymentsParams, Payment } from '@shared/types/entities';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Banknote,
  ChevronLeft,
  ChevronRight,
  Download,
  Euro,
  Eye,
  RotateCcw,
  TrendingUp,
  Wallet,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

// ─── Constants ──────────────────────────────────────────────────

const PAGE_SIZE = 25;

/**
 * Taux effectif réellement prélevé = commission / montant (en %, 1 décimale).
 * On NE l'estime PAS depuis `commissionRate` (= 2 % nominal stocké) car, depuis
 * la formule plancher/fixe, ce taux ne reflète plus le prélèvement réel.
 * Source de vérité = `applicationFeeAmount` (lu de Stripe). Renvoie null si
 * indéterminable (montant nul ou commission absente).
 */
function effectiveRatePct(amount: number, fee: number | null | undefined): string | null {
  if (fee == null || amount <= 0) return null;
  return ((fee / amount) * 100).toFixed(1);
}

// ─── Config ──────────────────────────────────────────────────────

type SortKey = 'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc';

const SORT_OPTIONS: { value: SortKey; label: string; sortBy: string; sortOrder: 'asc' | 'desc' }[] = [
  { value: 'date_desc', label: 'Date — récente', sortBy: 'createdAt', sortOrder: 'desc' },
  { value: 'date_asc', label: 'Date — ancienne', sortBy: 'createdAt', sortOrder: 'asc' },
  { value: 'amount_desc', label: 'Montant — décroissant', sortBy: 'amount', sortOrder: 'desc' },
  { value: 'amount_asc', label: 'Montant — croissant', sortBy: 'amount', sortOrder: 'asc' },
];

const VALID_STATUSES = new Set<string>(Object.values(PaymentStatus));
const VALID_SORTS = new Set<string>(SORT_OPTIONS.map(o => o.value));

// ─── Helpers ─────────────────────────────────────────────────────

async function fetchAllPaymentsForExport(params: Omit<GetPaymentsParams, 'page' | 'limit'>): Promise<Payment[]> {
  const limit = 100;
  let page = 1;
  const all: Payment[] = [];
  while (true) {
    const res = await getPaymentsApi({ ...params, limit, page });
    all.push(...res.data);
    if (page >= res.meta.totalPages) break;
    page++;
  }
  return all;
}

// ─── URL param helpers ─────────────────────────────────────────────────────

function applyParamUpdates(prev: URLSearchParams, updates: Record<string, string | undefined>): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined || value === '') next.delete(key);
    else next.set(key, value);
  }
  return next;
}

// ─── Payment Detail Modal ────────────────────────────────────────

interface PaymentDetailModalProps {
  paymentId: number | null;
  onClose: () => void;
}

function PaymentDetailModal({ paymentId, onClose }: PaymentDetailModalProps) {
  const qc = useQueryClient();
  const [confirmRefund, setConfirmRefund] = useState(false);

  const { data: payment, isLoading } = useQuery<Payment>({
    queryKey: queryKeys.payments.detail(paymentId!),
    queryFn: () => getPaymentApi(paymentId!),
    enabled: paymentId !== null,
  });

  const refundMutation = useMutation({
    mutationFn: (id: number) => refundPaymentApi(id),
    onSuccess: () => {
      toast.success('Remboursement Stripe déclenché');
      setConfirmRefund(false);
      qc.invalidateQueries({ queryKey: queryKeys.payments.detail(paymentId!) });
      qc.invalidateQueries({ queryKey: ['payments'] });
      qc.invalidateQueries({ queryKey: ['admin', 'refund-requests'] });
    },
    onError: () => toast.error('Erreur lors du remboursement'),
  });

  const canRefund = payment && payment.status === PaymentStatus.COMPLETED && !payment.refundedAt;

  if (!paymentId) return null;

  const cfg = payment ? PAY_STATUS_CONFIG[payment.status] : null;

  return (
    <div className='fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4'>
      <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={onClose} />

      <div className='relative z-10 w-full sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl'>
        {/* Header */}
        <div className='sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl'>
          <div>
            <h2 className='text-lg font-bold text-slate-900'>Détail du paiement</h2>
            {payment && <p className='text-xs font-mono text-slate-400 mt-0.5'>{payment.uid}</p>}
          </div>
          <button onClick={onClose} className='p-2 rounded-lg hover:bg-slate-100 transition-colors'>
            <X className='w-5 h-5 text-slate-500' />
          </button>
        </div>

        {isLoading && (
          <div className='p-6 space-y-4'>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className='h-12 animate-pulse rounded-xl bg-slate-100' />
            ))}
          </div>
        )}

        {!isLoading && payment && (
          <div className='p-6 space-y-5'>
            {/* Status + amount */}
            <div className='flex items-center gap-3 flex-wrap'>
              {cfg && (
                <span
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${cfg.bg} ${cfg.text}`}
                >
                  {cfg.label}
                </span>
              )}
              <span className='text-2xl font-bold text-slate-900'>{formatEur(payment.amount)}</span>
            </div>

            {/* Competition + Team + Org */}
            <div className='grid grid-cols-3 gap-3'>
              <div className='bg-slate-50 rounded-xl p-3'>
                <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1'>Concours</p>
                <p className='text-sm font-semibold text-slate-900 leading-snug'>{payment.competition?.name ?? '—'}</p>
                {payment.competition?.date && (
                  <p className='text-xs text-slate-400 mt-1'>{formatDateAbbreviatedFr(payment.competition.date)}</p>
                )}
              </div>
              <div className='bg-slate-50 rounded-xl p-3'>
                <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1'>Équipe</p>
                <p className='text-sm font-semibold text-slate-900 leading-snug'>{payment.team?.name ?? '—'}</p>
              </div>
              <div className='bg-slate-50 rounded-xl p-3'>
                <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1'>Organisation</p>
                <p className='text-sm font-semibold text-slate-900 leading-snug'>{payment.organization?.name ?? '—'}</p>
              </div>
            </div>

            {/* Financial breakdown */}
            <div className='bg-slate-50 rounded-xl p-4 space-y-2.5'>
              <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide'>Détail financier</p>
              <div className='flex justify-between text-sm'>
                <span className='text-slate-600'>Montant brut</span>
                <span className='font-semibold text-slate-900'>{formatEur(payment.amount)}</span>
              </div>
              {payment.applicationFeeAmount != null && (
                <>
                  <div className='flex justify-between text-sm'>
                    <span className='text-slate-600'>
                      Commission BoulesConnect
                      {effectiveRatePct(payment.amount, payment.applicationFeeAmount) != null && (
                        <span className='ml-1 text-xs text-slate-400'>
                          ({effectiveRatePct(payment.amount, payment.applicationFeeAmount)}%)
                        </span>
                      )}
                    </span>
                    <span className='font-semibold text-rose-600'>−{formatEur(payment.applicationFeeAmount)}</span>
                  </div>
                  <div className='flex justify-between text-sm border-t border-slate-200 pt-2.5'>
                    <span className='font-semibold text-slate-700'>Net organisateur</span>
                    <span className='font-bold text-emerald-700'>
                      {formatEur(payment.amount - payment.applicationFeeAmount)}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Refund block */}
            {payment.status === PaymentStatus.REFUNDED && (
              <div className='bg-purple-50 rounded-xl p-4 space-y-1.5'>
                <p className='text-xs font-semibold text-purple-700 uppercase tracking-wide'>Remboursement</p>
                {payment.refundAmount != null && (
                  <p className='text-sm font-semibold text-purple-900'>{formatEur(payment.refundAmount)}</p>
                )}
                {payment.refundedAt && (
                  <p className='text-xs text-purple-600'>Le {formatDateAbbreviatedFr(payment.refundedAt)}</p>
                )}
              </div>
            )}

            {/* Refund trigger */}
            {canRefund && !confirmRefund && (
              <button
                onClick={() => setConfirmRefund(true)}
                className='w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-semibold transition'
              >
                <RotateCcw className='w-4 h-4' />
                Rembourser via Stripe
              </button>
            )}

            {canRefund && confirmRefund && (
              <div className='bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3'>
                <div className='flex items-start gap-2'>
                  <AlertTriangle className='w-5 h-5 text-amber-600 shrink-0 mt-0.5' />
                  <div>
                    <p className='text-sm font-semibold text-amber-900'>Confirmer le remboursement ?</p>
                    <div className='text-xs text-amber-700 mt-1 space-y-1'>
                      <p>
                        Le joueur sera intégralement remboursé de{' '}
                        <span className='font-semibold'>{formatEur(payment.amount)}</span> via Stripe.
                      </p>
                      {payment.applicationFeeAmount != null && payment.applicationFeeAmount > 0 && (
                        <p>
                          La commission BoulesConnect (
                          <span className='font-semibold'>{formatEur(payment.applicationFeeAmount)}</span>) sera
                          restituée sur le solde de l&apos;organisateur.
                        </p>
                      )}
                      <p>
                        Les frais de traitement Stripe ne sont pas remboursés et restent à la charge de
                        l&apos;organisateur.
                      </p>
                      <p className='font-semibold'>Cette action est irréversible.</p>
                    </div>
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => refundMutation.mutate(payment.id)}
                    disabled={refundMutation.isPending}
                    className='flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition disabled:opacity-50'
                  >
                    {refundMutation.isPending ? 'Remboursement en cours...' : 'Confirmer'}
                  </button>
                  <button
                    onClick={() => setConfirmRefund(false)}
                    className='px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-50 transition'
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Stripe reference */}
            <div className='bg-slate-50 rounded-xl p-4'>
              <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2'>Référence Stripe</p>
              <p className='text-xs font-mono text-slate-700 break-all leading-relaxed'>
                {payment.stripePaymentIntentId}
              </p>
            </div>

            {/* Dates */}
            <div className='text-xs text-slate-400 space-y-1'>
              {payment.paidAt && <p>Payé le {formatDateAbbreviatedFr(payment.paidAt)}</p>}
              <p>Créé le {formatDateAbbreviatedFr(payment.createdAt)}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────

export default function AdminPayments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  // ─── Read filters from URL ───
  const urlStatus = searchParams.get('status') ?? '';
  const urlOrg = searchParams.get('org') ?? '';
  const urlSort = searchParams.get('sort') ?? 'date_desc';
  const urlPage = Math.max(1, Number(searchParams.get('page') ?? '1'));

  const statusFilter = (VALID_STATUSES.has(urlStatus) ? urlStatus : '') as PaymentStatus | '';
  const orgFilter = urlOrg ? Number(urlOrg) || '' : ('' as number | '');
  const sortKey = (VALID_SORTS.has(urlSort) ? urlSort : 'date_desc') as SortKey;
  const page = urlPage;

  // ─── Modal (auto-open via ?focus=paymentId from AdminRefundRequests) ───
  const [openPaymentId, setOpenPaymentId] = useState<number | null>(() => {
    const f = searchParams.get('focus');
    if (f) {
      const id = Number(f);
      if (id > 0) return id;
    }
    return null;
  });

  // ─── Export ───
  const [exporting, setExporting] = useState(false);

  function updateParams(updates: Record<string, string | undefined>) {
    setSearchParams(prev => applyParamUpdates(prev, updates), { replace: true });
  }

  // ─── Sort params ───
  const sortOpt = SORT_OPTIONS.find(o => o.value === sortKey) ?? SORT_OPTIONS[0];

  // ─── Orgs dropdown ───
  const { data: orgsData } = useQuery({
    queryKey: queryKeys.organizations.list({ limit: 100, sortBy: 'name', sortOrder: 'asc' }),
    queryFn: () => getOrganizationsApi({ limit: 100, sortBy: 'name', sortOrder: 'asc' }),
    staleTime: 10 * 60 * 1000,
    select: res => res.data,
    enabled: isAuthenticated,
  });
  const orgs = useMemo(() => orgsData ?? [], [orgsData]);

  // ─── Platform summary (reuses AdminStats cache) ───
  const { data: platformStats } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: getAdminStatsApi,
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  });

  // ─── Payments list (paginated, with filters) ───
  const listParams: GetPaymentsParams = {
    ...(statusFilter && { status: statusFilter }),
    ...(orgFilter && { organizationId: Number(orgFilter) }),
    sortBy: sortOpt.sortBy,
    sortOrder: sortOpt.sortOrder,
    page,
    limit: PAGE_SIZE,
  };

  const {
    data: listData,
    isLoading: listLoading,
    isFetching: listFetching,
    isError: listError,
    refetch: refetchList,
  } = useQuery({
    queryKey: queryKeys.payments.list(listParams as Record<string, unknown>),
    queryFn: () => getPaymentsApi(listParams),
    placeholderData: keepPreviousData,
    enabled: isAuthenticated,
  });

  const payments: Payment[] = listData?.data ?? [];
  const total = listData?.meta?.total ?? 0;
  const totalPages = listData?.meta?.totalPages ?? 1;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  // ─── Export ───
  const paymentExportColumns: ExportColumn<Payment>[] = [
    {
      header: 'Date',
      value: p => (p.paidAt ? formatDateAbbreviatedFr(p.paidAt) : formatDateAbbreviatedFr(p.createdAt)),
    },
    { header: 'Référence', value: 'uid' },
    { header: 'Concours', value: p => p.competition?.name ?? '' },
    { header: 'Équipe', value: p => p.team?.name ?? '' },
    { header: 'Organisation', value: p => p.organization?.name ?? '' },
    { header: 'Montant (€)', value: p => p.amount / 100, numFmt: '#,##0.00' },
    { header: 'Commission (€)', value: p => (p.applicationFeeAmount ?? 0) / 100, numFmt: '#,##0.00' },
    { header: 'Taux effectif (%)', value: p => effectiveRatePct(p.amount, p.applicationFeeAmount) ?? '' },
    { header: 'Net org. (€)', value: p => (p.amount - (p.applicationFeeAmount ?? 0)) / 100, numFmt: '#,##0.00' },
    { header: 'Statut', value: p => PAY_STATUS_CONFIG[p.status].label },
  ];

  async function handleExport(format: ExportFormat = 'csv') {
    setExporting(true);
    try {
      const exportParams: Omit<GetPaymentsParams, 'page' | 'limit'> = {
        ...(statusFilter && { status: statusFilter }),
        ...(orgFilter && { organizationId: Number(orgFilter) }),
        sortBy: sortOpt.sortBy,
        sortOrder: sortOpt.sortOrder,
      };
      const rows = await fetchAllPaymentsForExport(exportParams);
      await exportData(rows, paymentExportColumns, datedFilename('paiements-plateforme', format), format, {
        sheetName: 'Paiements',
      });
      toast.success(`${rows.length} paiements exportés`);
    } catch {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  }

  const hasFilters = statusFilter !== '' || orgFilter !== '';
  const isPageLoading = listLoading || !isAuthenticated;

  return (
    <DashboardLayout sidebarConfig={adminSidebar}>
      <div className='p-8 space-y-6'>
        <PageHeader
          title='Paiements plateforme'
          subtitle='Tous les flux financiers BoulesConnect'
          actions={
            <div className='flex items-center gap-2'>
              <button
                onClick={() => void handleExport('csv')}
                disabled={exporting}
                className='flex items-center gap-2 px-5 py-2 bg-white border border-slate-200 rounded-xl hover:border-blue-300 transition disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <Download className='w-4 h-4 text-slate-600' />
                <span className='text-sm font-semibold text-slate-700'>{exporting ? 'Export…' : 'CSV'}</span>
              </button>
              <button
                onClick={() => void handleExport('xlsx')}
                disabled={exporting}
                className='flex items-center gap-2 px-5 py-2 bg-white border border-slate-200 rounded-xl hover:border-blue-300 transition disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <Download className='w-4 h-4 text-slate-600' />
                <span className='text-sm font-semibold text-slate-700'>{exporting ? 'Export…' : 'Excel'}</span>
              </button>
            </div>
          }
        />

        {/* ── Stats cards ── */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5'>
          <StatCard
            icon={Euro}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Volume total encaissé'
            value={platformStats ? formatEur(platformStats.revenue.totalVolumeCents) : '—'}
            subtitle={`${platformStats?.revenue.totalPaidPayments ?? 0} paiements complétés`}
          />
          <StatCard
            icon={TrendingUp}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Commissions BoulesConnect'
            value={platformStats ? formatEur(platformStats.revenue.totalFeeCents) : '—'}
            subtitle={`Cette année : ${platformStats ? formatEur(platformStats.revenue.thisYearFeeCents) : '—'}`}
          />
          <StatCard
            icon={Banknote}
            iconBg='bg-rose-100'
            iconColor='text-rose-600'
            label='Ce mois — commissions'
            value={platformStats ? formatEur(platformStats.revenue.thisMonthFeeCents) : '—'}
            subtitle={`Total transactions : ${total.toLocaleString('fr-FR')}`}
          />
          <StatCard
            icon={RotateCcw}
            iconBg='bg-purple-100'
            iconColor='text-purple-600'
            label='Inscriptions ce mois'
            value={platformStats ? String(platformStats.registrations.thisMonth) : '—'}
            subtitle={`Confirmées : ${platformStats?.registrations.confirmed ?? 0}`}
          />
        </div>

        {listError && <QueryErrorState onRetry={() => void refetchList()} />}

        {/* ── Table ── */}
        <div className='bg-white rounded-2xl border border-slate-200'>
          {/* Filters bar */}
          <div className='flex items-center justify-between flex-wrap gap-3 px-6 py-4 border-b border-slate-200'>
            <div className='flex items-center gap-2'>
              <h2 className='text-lg font-bold text-slate-900'>Historique</h2>
              {total > 0 && (
                <span className='px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full'>
                  {total.toLocaleString('fr-FR')}
                </span>
              )}
              {listFetching && !isPageLoading && <span className='text-xs text-slate-400 ml-1'>Actualisation…</span>}
            </div>

            <div className='flex items-center gap-2 flex-wrap'>
              {/* Statut */}
              <select
                value={statusFilter}
                onChange={e => updateParams({ status: e.target.value || undefined, page: undefined })}
                className='px-3 py-2 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm text-slate-700 bg-white'
              >
                <option value=''>Tous les statuts</option>
                {Object.entries(PAY_STATUS_CONFIG).map(([value, cfg]) => (
                  <option key={value} value={value}>
                    {cfg.label}
                  </option>
                ))}
              </select>

              {/* Organisation */}
              <select
                value={orgFilter}
                onChange={e => updateParams({ org: e.target.value || undefined, page: undefined })}
                className='px-3 py-2 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm text-slate-700 bg-white max-w-[200px]'
              >
                <option value=''>Toutes les orgas</option>
                {orgs.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>

              {/* Sort */}
              <select
                value={sortKey}
                onChange={e => updateParams({ sort: e.target.value, page: undefined })}
                className='px-3 py-2 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm text-slate-700 bg-white'
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              {/* Clear filters */}
              {hasFilters && (
                <button
                  onClick={() => updateParams({ status: undefined, org: undefined, page: undefined })}
                  className='px-3 py-2 text-xs font-semibold text-slate-500 hover:text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50 transition'
                >
                  Effacer les filtres
                </button>
              )}
            </div>
          </div>

          {/* Table header */}
          <div className='hidden lg:grid lg:grid-cols-12 gap-3 px-6 py-3 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider'>
            <div className='col-span-2'>Date</div>
            <div className='col-span-2'>Concours</div>
            <div className='col-span-2'>Équipe</div>
            <div className='col-span-2'>Organisation</div>
            <div className='col-span-1 text-right'>Montant</div>
            <div className='col-span-2 text-right'>Commission</div>
            <div className='col-span-1 text-right'>Statut</div>
          </div>

          {/* Table body */}
          {isPageLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className='grid grid-cols-12 gap-3 px-6 py-4 border-b border-slate-50'>
                <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-1 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-1 h-4 animate-pulse rounded bg-slate-200' />
              </div>
            ))
          ) : payments.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-16 text-slate-400 gap-3'>
              <Wallet className='w-10 h-10 opacity-30' />
              <p className='text-sm font-medium'>
                Aucun paiement{hasFilters ? ' avec ces filtres' : " pour l'instant"}
              </p>
            </div>
          ) : (
            payments.map(payment => {
              const cfg = PAY_STATUS_CONFIG[payment.status];
              const dateStr = payment.paidAt
                ? formatDateAbbreviatedFr(payment.paidAt)
                : formatDateAbbreviatedFr(payment.createdAt);
              const fee = payment.applicationFeeAmount;

              return (
                <div
                  key={payment.id}
                  className='grid grid-cols-12 gap-3 px-6 py-3.5 border-b border-slate-50 items-center hover:bg-slate-50/70 transition group'
                >
                  {/* Date */}
                  <div className='col-span-2'>
                    <p className='text-sm text-slate-600'>{dateStr}</p>
                    <p className='text-[10px] font-mono text-slate-400 mt-0.5 truncate'>{payment.uid}</p>
                  </div>

                  {/* Concours */}
                  <div className='col-span-2 min-w-0'>
                    <p className='text-sm font-medium text-slate-900 truncate'>{payment.competition?.name ?? '—'}</p>
                    {payment.competition?.date && (
                      <p className='text-xs text-slate-400 mt-0.5'>
                        {formatDateAbbreviatedFr(payment.competition.date)}
                      </p>
                    )}
                  </div>

                  {/* Équipe */}
                  <div className='col-span-2 min-w-0'>
                    <p className='text-sm text-slate-700 truncate'>{payment.team?.name ?? '—'}</p>
                  </div>

                  {/* Organisation */}
                  <div className='col-span-2 min-w-0'>
                    <p className='text-sm text-slate-700 truncate'>{payment.organization?.name ?? '—'}</p>
                  </div>

                  {/* Montant */}
                  <div className='col-span-1 text-right'>
                    <p className='text-sm font-bold text-slate-900'>{formatEur(payment.amount)}</p>
                  </div>

                  {/* Commission */}
                  <div className='col-span-2 text-right'>
                    {fee != null && fee > 0 ? (
                      <>
                        <p className='text-sm font-semibold text-emerald-700'>{formatEur(fee)}</p>
                        {effectiveRatePct(payment.amount, fee) != null && (
                          <p className='text-xs text-slate-400'>{effectiveRatePct(payment.amount, fee)}%</p>
                        )}
                      </>
                    ) : (
                      <p className='text-xs text-slate-300'>—</p>
                    )}
                  </div>

                  {/* Statut + Détail */}
                  <div className='col-span-1 flex items-center justify-end gap-2'>
                    <span
                      className={`hidden xl:inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}
                    >
                      {cfg.label}
                    </span>
                    <button
                      onClick={() => setOpenPaymentId(payment.id)}
                      className='p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition opacity-0 group-hover:opacity-100'
                    >
                      <Eye className='w-4 h-4' />
                    </button>
                  </div>
                </div>
              );
            })
          )}

          {/* Status legend (mobile visible rows) */}
          {!isPageLoading && payments.length > 0 && (
            <div className='xl:hidden px-6 py-2 flex items-center gap-3 flex-wrap'>
              {Object.entries(PAY_STATUS_CONFIG).map(([v, cfg]) => (
                <span key={v} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                  {cfg.label}
                </span>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > 0 && (
            <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
              <p className='text-sm text-slate-500'>
                <span className='font-semibold text-slate-700'>{from}</span>
                {' – '}
                <span className='font-semibold text-slate-700'>{to}</span>
                {' sur '}
                <span className='font-semibold text-slate-700'>{total.toLocaleString('fr-FR')}</span>
              </p>
              {totalPages > 1 && (
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => updateParams({ page: String(Math.max(1, page - 1)) })}
                    disabled={page === 1}
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </button>
                  {getPageNumbers(page, totalPages).map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} className='px-2 text-slate-400'>
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => updateParams({ page: String(p) })}
                        className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                          p === page ? 'bg-blue-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() => updateParams({ page: String(Math.min(totalPages, page + 1)) })}
                    disabled={page >= totalPages}
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <PaymentDetailModal
        paymentId={openPaymentId}
        onClose={() => {
          setOpenPaymentId(null);
          if (searchParams.has('focus')) {
            setSearchParams(
              prev => {
                const next = new URLSearchParams(prev);
                next.delete('focus');
                return next;
              },
              { replace: true },
            );
          }
        }}
      />
    </DashboardLayout>
  );
}
